import React from "react";

export type SectionShellProps = {
  id: string;
  /** Optional additive layout classes for a specific section shell. */
  className?: string;
  /**
   * Widened from string to ReactNode for the Emails numbered variant's gray
   * "(Optional)" suffix (B10) — same additive pattern as the description
   * widening below; string consumers emit byte-identical DOM.
   */
  title: React.ReactNode;
  children: React.ReactNode;
  /**
   * Rendered inside the description <p> below the pill/heading. Widened from
   * string to ReactNode for the InvoiceSection facade (B5/Q-034); string
   * consumers (CreateMember et al.) emit byte-identical DOM.
   */
  description?: React.ReactNode;
  /**
   * "numbered" (B10, Emails wizard steps 1-2 / POL-002): gold circle number
   * badge + large extrabold heading + description — the transcription-bound
   * Emails section header (emails-steps12.md "small gold circle badges") —
   * inside the same shared black-border card. Additive-only: "pill"/"card"
   * consumers emit byte-identical DOM.
   */
  variant?: "pill" | "overlapPill" | "card" | "analytics" | "numbered";
  /** Badge number for variant="numbered". */
  number?: number;
  /**
   * variant="numbered" only: right-aligned header actions (Emails step 1
   * "Clear All Filters"); wraps the heading + actions in the donor's
   * flex-col/lg:flex-row row. Ignored by "pill"/"card".
   */
  headerActions?: React.ReactNode;
  /** Semantic heading level for nested analytics cards. */
  analyticsHeadingLevel?: 3 | 4;
};

// Numbered header markup is a verbatim copy of the Emails SectionHeading
// donor (emailWizardShared.tsx); class strings are inlined because
// common/dashboard must not import from src/components/pages/** (layering
// invariant).
const NumberedHeading = ({
  number,
  title,
  description,
}: {
  number?: number;
  title: React.ReactNode;
  description?: React.ReactNode;
}) => (
  <div className="mb-6 flex items-start gap-3">
    <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cpaGold text-[15px] font-extrabold text-white">
      {number}
    </span>
    <div>
      <h2 className="text-[26px] font-extrabold leading-tight text-gray-950">
        {title}
      </h2>
      {description ? (
        <p className="mt-2 text-[16px] leading-6 text-black">{description}</p>
      ) : null}
    </div>
  </div>
);

export const SectionShell = ({
  id,
  className = "",
  title,
  description,
  children,
  variant = "pill",
  number,
  headerActions,
  analyticsHeadingLevel = 3,
}: SectionShellProps) => {
  const isOverlapPill = variant === "overlapPill";
  const isAnalyticsCard = variant === "analytics";
  const AnalyticsHeading = analyticsHeadingLevel === 4 ? "h4" : "h3";

  return (
    <section
      id={id}
      className={
        isOverlapPill
          ? `relative mt-[50px] scroll-mt-28 rounded-2xl border border-[#9E9E9E] bg-white px-4 pb-5 pt-10 shadow-none min-[960px]:px-[35px] min-[960px]:pb-[35px] min-[960px]:pt-12 ${className}`
          : isAnalyticsCard
            ? `scroll-mt-28 rounded-2xl bg-white p-5 shadow-[0_6px_24px_rgba(15,23,42,0.07)] sm:p-6 ${className}`
          : `scroll-mt-28 rounded-lg border border-black bg-white p-6 shadow-none ${className}`
      }
    >
      {variant === "numbered" ? (
        headerActions ? (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <NumberedHeading
              number={number}
              title={title}
              description={description}
            />
            {headerActions}
          </div>
        ) : (
          <NumberedHeading
            number={number}
            title={title}
            description={description}
          />
        )
      ) : isOverlapPill ? (
        <div>
          <span className="absolute -top-[22px] left-3 inline-flex max-w-[calc(100%-24px)] rounded-full bg-cpaGold px-[18px] py-[10px] text-sm font-bold leading-[18px] text-white min-[960px]:-top-7 min-[960px]:left-[26px] min-[960px]:px-[35px] min-[960px]:py-[15px] min-[960px]:text-xl min-[960px]:leading-6">
            {title}
          </span>
          {description ? (
            <p className="mb-5 text-sm leading-[1.5] text-black min-[960px]:text-base">
              {description}
            </p>
          ) : null}
        </div>
      ) : isAnalyticsCard ? (
        <div className="mb-5 flex flex-col gap-1.5">
          <AnalyticsHeading className="text-xl font-extrabold leading-7 text-slate-950 sm:text-2xl">
            {title}
          </AnalyticsHeading>
          {description ? (
            <p className="max-w-5xl text-sm leading-6 text-slate-600">
              {description}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mb-5 flex flex-col gap-2">
          {variant === "pill" ? (
            // Pill text is white on gold (July 9, 2026 direction).
            <span className="inline-flex w-fit max-w-full rounded-full bg-cpaGold px-5 py-2 text-sm font-bold text-white">
              {title}
            </span>
          ) : (
            <h3 className="text-lg font-bold leading-6 text-black">{title}</h3>
          )}
          {description && (
            <p className="max-w-5xl text-sm leading-6 text-black">
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </section>
  );
};
